import { IUser } from './user';
import { AccountType } from '../../../shared/types/user';

/**
 * Core authentication types
 */

/** Base credentials interface with common auth fields */
export interface ILoginCredentials {
  email: string;
  password: string;
  rememberMe?: boolean;
}

export interface IAppleLoginCredentials {
  identityToken: string;
  authorizationCode?: string;
  email?: string;
  fullName?: {
    givenName?: string;
    familyName?: string;
  };
}

/** Extended credentials interface for registration */
export interface IRegisterCredentials extends ILoginCredentials {
  username: string;
  name: string;
  confirmPassword: string;
  acceptTerms: boolean;
  ageConfirmed: boolean;
  accountType?: AccountType; // Two-tier user system
  businessName?: string; // Optional for service providers
}

/**
 * Password management types
 */

/** Request to initiate password reset */
export interface IPasswordResetRequest {
  email: string;
}

/** Confirm password reset with token */
export interface IPasswordResetConfirmation {
  token: string;
  password: string;
  confirmPassword: string;
}

/** Request to change current password */
export interface IPasswordChangeRequest {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

/** Request to verify email address */
export interface IEmailVerificationRequest {
  token: string;
}

/**
 * API Response types
 */

/** Standard authentication response */
export interface IAuthResponse {
  success: boolean;
  message?: string;
  data: {
    token: string;
    refreshToken: string;
    expiresIn: number;
    user: IUser;
    requiresBusinessSetup?: boolean; // Indicator for service providers
  };
}

/**
 * Authentication result
 */
export interface IAuthResult {
  user: IUser;
  token: string;
  refreshToken: string;
  expiresIn: number;
}

/** A verified provider identity awaiting an explicit account decision. */
export interface IPendingSocialAuth {
  requiresAccountConfirmation: true;
  pendingToken: string;
  provider: 'apple' | 'google';
  expiresAt: string;
}

export type SocialAuthResult = IAuthResult | IPendingSocialAuth;

export type SocialAuthCompletion =
  | { pendingToken: string; action: 'create'; termsAccepted: boolean; termsVersion: string; ageConfirmed: boolean }
  | { pendingToken: string; action: 'link'; email: string; password: string };

/**
 * Token payload
 */
export interface ITokenPayload {
  userId: string;
  email: string;
  role: string;
  iat: number;
  exp: number;
}

/**
 * OAuth types
 */

/** OAuth provider information */
export interface IOAuthProvider {
  id: string;
  name: string;
  connected: boolean;
  email?: string;
  avatar?: string;
}

/** OAuth connection response */
export interface IOAuthConnectResponse {
  provider: IOAuthProvider;
  connected: boolean;
}

/** OAuth login response */
export interface IOAuthLoginResponse extends IAuthResponse {
  isNewUser: boolean;
  provider: string;
}

/**
 * Two-factor authentication types
 */

/** Response for 2FA setup */
export interface ITwoFactorSetupResponse {
  success: boolean;
  message?: string;
  data: {
    secret: string;
    qrCodeUrl: string;
    recoveryCodes: string[];
  };
}

/** Request to verify 2FA code */
export interface ITwoFactorVerifyRequest {
  code: string;
  rememberDevice?: boolean;
}

/** Login response when 2FA is required */
export interface ITwoFactorLoginResponse extends IAuthResponse {
  requiresTwoFactor: boolean;
  tempToken?: string;
}

/**
 * Session management
 */

/** Session information */
export interface ISession {
  id: string;
  device: string;
  browser: string;
  location: string;
  ip: string;
  lastActive: string;
  current: boolean;
}

/** Response for session list */
export interface ISessionResponse {
  sessions: ISession[];
  currentSession: ISession;
}

/**
 * Security types
 */

/** User security settings */
export interface ISecuritySettings {
  twoFactorEnabled: boolean;
  recoveryCodesRemaining: number;
  passwordLastChanged: string;
  activeDevices: number;
}

/** Login attempt record */
export interface ILoginAttempt {
  id: string;
  timestamp: string;
  success: boolean;
  ip: string;
  location: string;
  device: string;
  browser: string;
}

/** Login history information */
export interface ILoginHistory {
  attempts: ILoginAttempt[];
  suspicious: ILoginAttempt[];
}

/**
 * Auth state types
 */

/** Possible authentication states */
export enum AuthStatus {
  IDLE = 'idle',
  AUTHENTICATING = 'authenticating',
  AUTHENTICATED = 'authenticated',
  ERROR = 'error',
  TWO_FACTOR_REQUIRED = 'twoFactorRequired',
  EMAIL_VERIFICATION_REQUIRED = 'emailVerificationRequired'
}

/** Auth context state */
export interface IAuthState {
  user: IUser | null;
  status: AuthStatus;
  error: string | null;
  loading: boolean;
  initialized: boolean;
}

/**
 * Error handling
 */

/** Custom auth error with additional properties */
export class AuthError extends Error {
  constructor(
    message: string,
    public code?: string,
    public field?: string,
    public status?: number,
    public validationErrors?: Record<string, string>
  ) {
    super(message);
    this.name = 'AuthError';
  }
}

/** Field validation error */
export interface IValidationError {
  field: string;
  message: string;
}

/** API error response */
export interface IApiErrorResponse {
  message: string;
  code?: string;
  status?: number;
  errors?: IValidationError[];
}

/**
 * Permission types
 */

/** Available auth permissions */
export enum AuthPermission {
  READ_PROFILE = 'read:profile',
  WRITE_PROFILE = 'write:profile',
  DELETE_ACCOUNT = 'delete:account',
  MANAGE_SESSIONS = 'manage:sessions',
  MANAGE_SECURITY = 'manage:security'
}

/** Auth role with permissions */
export interface IAuthRole {
  id: string;
  name: string;
  permissions: AuthPermission[];
}

/**
 * Token types
 */

/** Auth token information */
export interface IAuthToken {
  token: string;
  refreshToken: string;
  expiresAt: number;
}

/** Auth request headers */
export interface IAuthHeaders {
  Authorization: string;
  'X-Client-ID'?: string;
  'X-Device-ID'?: string;
}

// Re-exports for backward compatibility
// Re-exports for backward compatibility
export type { IUser };
export type { IUser as AuthUser };

/**
 * Token and header types
 */
export interface IAuthToken {
  token: string;
  refreshToken: string;
  expiresAt: number;
}

export interface IAuthHeaders {
  Authorization: string;
  'X-Client-ID'?: string;
  'X-Device-ID'?: string;
}

// Additional interfaces for backward compatibility
export interface ResetPasswordRequest {
  email: string;
}

export interface ResetPasswordConfirmation {
  token: string;
  newPassword: string;
  confirmPassword: string;
}
